import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { money, moneyCents, formatApiError, fmtInput, toNum } from "../lib/api";
import Shell from "../components/Shell";
import { useAuth } from "../context/AuthContext";
import ClientLedger from "../components/ClientLedger";
import { PdfPreviewModal } from "../components/PdfPreviewModal";
import { statusMeta } from "../lib/status";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, ArrowLeft, Download, Mail, AlertTriangle, CheckCircle2, Ban, Phone, Eye, MessageSquare, FileText, Zap, Trash2, Pencil } from "lucide-react";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { FeeBreakdownModal } from "../components/FeeBreakdownModal";
import { LightningRequestDialog } from "../components/LightningRequestDialog";
import { LightningRequests } from "../components/LightningRequests";
import { maxConcessionPct } from "../lib/concessions";
import { playThunder } from "../lib/thunder";
import { toast } from "sonner";

const parseCc = (raw) =>
  (raw || "").split(/[,\s;]+/).map((x) => x.trim()).filter((x) => x.includes("@"));

function Field({ label, prefix, ...props }) {
  return (
    <div className="space-y-2">
      <Label className="text-xs tracking-[0.15em] uppercase text-white/50">{label}</Label>
      <div className="relative">
        {prefix && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 text-sm">{prefix}</span>
        )}
        <Input
          {...props}
          className={`bg-black/40 border-white/15 h-11 text-white focus:ring-2 focus:ring-[#ff3b30] ${
            prefix ? "pl-7" : ""
          }`}
        />
      </div>
    </div>
  );
}

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, actAs } = useAuth();
  const isLenderView = user?.role === "admin" && !actAs;
  // Only lenders (in their own view or acting as an agent) may generate/send loan estimates.
  const canLoanEstimate = user?.role === "admin";
  const backTo = isLenderView ? "/lender" : "/dashboard";
  const [client, setClient] = useState(null);
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({ address: "", offer_price: "", property_taxes: "", hoa: "", cc: "", concessions: "" });
  const [toggles, setToggles] = useState({ sendPre: false, genEst: true, sendEst: false });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");
  const [autoCheckin, setAutoCheckin] = useState(true);
  const [requestingMax, setRequestingMax] = useState(false);
  const [ledgerVersion, setLedgerVersion] = useState(0);
  const [preview, setPreview] = useState(null);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [showLightning, setShowLightning] = useState(false);
  const [lightningReqs, setLightningReqs] = useState(null);
  const [fulfillId, setFulfillId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editForm, setEditForm] = useState({});

  const openEdit = () => {
    setEditForm({
      max_purchase_price: fmtInput(String(client.max_purchase_price ?? "")),
      interest_rate: String(client.interest_rate ?? ""),
      apr: String(client.apr ?? ""),
      loan_term_years: String(client.loan_term_years ?? ""),
      down_payment_pct: String(client.down_payment_pct ?? ""),
      loan_type: client.loan_type ?? "",
      insurance_monthly: fmtInput(String(client.insurance_monthly ?? "")),
    });
    setShowEdit(true);
  };
  const setEdit = (k, isMoney = false) => (e) =>
    setEditForm((f) => ({ ...f, [k]: isMoney ? fmtInput(e.target.value) : e.target.value }));
  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      const fd = new FormData();
      fd.append("max_purchase_price", String(toNum(editForm.max_purchase_price)));
      fd.append("interest_rate", String(parseFloat(editForm.interest_rate) || 0));
      fd.append("apr", String(parseFloat(editForm.apr) || 0));
      fd.append("loan_term_years", String(parseInt(editForm.loan_term_years, 10) || 30));
      fd.append("down_payment_pct", String(parseFloat(editForm.down_payment_pct) || 0));
      if (editForm.loan_type) fd.append("loan_type", editForm.loan_type);
      fd.append("insurance_monthly", String(toNum(editForm.insurance_monthly)));
      const { data } = await api.patch(`/admin/clients/${id}`, fd);
      setClient(data);
      toast.success("Pre-approval updated 🍭");
      setShowEdit(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not update the pre-approval");
    } finally {
      setSavingEdit(false);
    }
  };

  const deleteClient = async () => {
    if (!window.confirm(`Delete ${client.borrower_name}?\n\nThis permanently removes this client and all of their pre-approvals and lightning requests. Use this to clear out a duplicate.`)) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/clients/${id}`);
      toast.success(`${client.borrower_name} deleted`);
      navigate(backTo);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not delete the client");
      setDeleting(false);
    }
  };

  const loadLightning = () =>
    api.get(`/clients/${id}/lightning-requests`).then((r) => setLightningReqs(r.data)).catch(() => setLightningReqs([]));

  useEffect(() => {
    loadLightning();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, ledgerVersion]);

  const buildFromRequest = (r) => {
    setFulfillId(r.id);
    setForm((f) => ({ ...f, address: r.address, offer_price: fmtInput(String(r.offer_price)) }));
    toast.success("Loaded lightning request — set taxes/HOA and generate \u26a1");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const markWorking = async (r) => {
    try {
      await api.post(`/clients/${id}/lightning-requests/${r.id}/working`);
      playThunder();
      toast.success("Approved — your agent has been alerted \u26a1");
      loadLightning();
    } catch (e) {
      toast.error("Could not update the request");
    }
  };
  const approveAndBuild = async (r) => {
    await markWorking(r);
    buildFromRequest(r);
  };
  const pendingLightning = (lightningReqs || []).filter((r) => r.status !== "fulfilled").length;

  // Agent side: pulse + thunder the first time a request turns "working" or "fulfilled".
  useEffect(() => {
    if (isLenderView || !lightningReqs) return;
    const key = "mpl_seen_lightning";
    let seen = {};
    try { seen = JSON.parse(localStorage.getItem(key) || "{}"); } catch { seen = {}; }
    let changed = false;
    let celebrate = false;
    lightningReqs.forEach((r) => {
      if (r.status === "working" || r.status === "fulfilled") {
        if (seen[r.id] !== r.status) {
          celebrate = true;
          seen[r.id] = r.status;
          changed = true;
        }
      }
    });
    if (celebrate) playThunder();
    if (changed) localStorage.setItem(key, JSON.stringify(seen));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightningReqs, isLenderView]);

  useEffect(() => {
    api.get(`/clients/${id}`).then((r) => {
      setClient(r.data);
      setAutoCheckin(r.data.auto_checkin !== false);
    }).catch(() => navigate(backTo));
    if (!isLenderView) api.get("/agent/status").then((r) => setStatus(r.data)).catch(() => {});
  }, [id, navigate, backTo, isLenderView]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setMoney = (k) => (e) => setForm({ ...form, [k]: fmtInput(e.target.value) });
  const blocked = status && (status.status !== "active" || !status.can_send);
  const overMax = client && form.offer_price !== "" && toNum(form.offer_price) > 0 &&
    toNum(form.offer_price) > Number(client.max_purchase_price || 0);

  const requestMaxIncrease = async () => {
    setRequestingMax(true);
    try {
      const { data } = await api.post(`/clients/${id}/request-max-increase`, {
        requested_amount: toNum(form.offer_price),
      });
      toast.success(`Request sent to your lender${data.to ? ` (${data.to})` : ""} 🍭`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setRequestingMax(false);
    }
  };

  const raiseMaxAndGenerate = async () => {
    setRequestingMax(true);
    try {
      const fd = new FormData();
      fd.append("max_purchase_price", String(toNum(form.offer_price)));
      const { data } = await api.patch(`/admin/clients/${id}`, fd);
      setClient(data);
      toast.success(`Approved max raised to ${money(data.max_purchase_price)} — generating ⚡`);
      await generate();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not raise the approved max");
    } finally {
      setRequestingMax(false);
    }
  };

  const toggleAutoCheckin = async (v) => {
    setAutoCheckin(v);
    try {
      await api.patch(`/clients/${id}/auto-checkin`, { enabled: v });
      toast.success(v ? "Auto 30-day check-in is ON 🍭" : "Auto 30-day check-in is OFF");
    } catch {
      setAutoCheckin(!v);
      toast.error("Could not update the check-in setting");
    }
  };

  const generate = async (e) => {
    e?.preventDefault?.();
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/preapprovals", {
        client_id: id,
        address: form.address,
        offer_price: toNum(form.offer_price),
        property_taxes: toNum(form.property_taxes),
        hoa: toNum(form.hoa),
        send_preapproval_to_buyer: toggles.sendPre,
        generate_loan_estimate: true,
        send_loan_estimate_to_buyer: false,
        seller_concessions: toNum(form.concessions),
        cc_emails: parseCc(form.cc),
        lightning_request_id: fulfillId || undefined,
      });
      setResult(data.preapproval);
      setLedgerVersion((v) => v + 1);
      if (fulfillId) {
        setFulfillId(null);
        loadLightning();
      }
      const s = data.email_status || {};
      if (s.preapproval_sent || s.estimate_sent)
        toast.success(`Sent to borrower${s.preapproval_sent ? " · pre-approval" : ""}${s.estimate_sent ? " · loan estimate" : ""}`);
      else toast.success("Pre-approval generated — download or send below");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const downloadBlob = async (path, filename, key) => {
    setBusy(key);
    try {
      const res = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not download PDF");
    } finally {
      setBusy("");
    }
  };

  const downloadPdf = () => downloadBlob(`/preapprovals/${result.id}/pdf`, `preapproval-${client.borrower_name}.pdf`, "pdf");
  const downloadEstimate = () => downloadBlob(`/preapprovals/${result.id}/loan-estimate/pdf`, `loan-estimate-${client.borrower_name}.pdf`, "est");

  const emailSelf = async () => {
    setBusy("email");
    try {
      await api.post(`/preapprovals/${result.id}/email`);
      toast.success("Pre-approval emailed to you");
    } catch (e) {
      toast.error("Could not send email");
    } finally {
      setBusy("");
    }
  };

  const shareUrl = result ? `${window.location.origin}/api/share/${result.id}` : "";
  const textBody = result ? `Here is ${client.borrower_name}'s pre-approval from My Pocket Lender:\n${shareUrl}` : "";
  const mailSubject = client ? `${client.borrower_name}'s Pre-Approval — My Pocket Lender` : "";
  const buyerPhone = (client?.borrower_phone || "").replace(/[^\d+]/g, "");
  const buyerEmail = client?.borrower_email || "";
  const smsHref = `sms:${buyerPhone}?&body=${encodeURIComponent(textBody)}`;
  const mailHref = result
    ? `mailto:${buyerEmail}?subject=${encodeURIComponent(mailSubject)}&body=${encodeURIComponent(textBody)}`
    : "#";

  const maxPct = client ? maxConcessionPct(client.loan_type, client.down_payment_pct, client.occupancy) : 0;
  const offerNum = toNum(form.offer_price);
  const basisPrice = offerNum > 0 ? offerNum : Number(client?.max_purchase_price || 0);
  const maxConcession = Math.round((basisPrice * maxPct) / 100);

  if (!client)
    return (
      <Shell>
        <div className="flex justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-[#ff3b30]" />
        </div>
      </Shell>
    );

  return (
    <Shell>
      <button
        onClick={() => navigate(backTo)}
        className="flex items-center gap-2 text-sm text-white/55 hover:text-white transition-colors mb-6"
        data-testid="back-button"
      >
        <ArrowLeft className="h-4 w-4" /> All clients
      </button>
      {isLenderView && (
        <div className="mb-4 text-xs text-white/40" data-testid="lender-view-note">
          You're the lender — build, edit and send approvals for this client right here.
        </div>
      )}

      {!isLenderView && !blocked && (
        <button
          type="button"
          onClick={() => setShowLightning(true)}
          data-testid="lightning-request-button"
          className="group relative w-full mb-6 overflow-hidden rounded-2xl border border-[#FFD23F]/50 bg-gradient-to-r from-[#1a1405] via-[#221a02] to-[#1a1405] px-5 py-4 text-left shadow-[0_0_28px_rgba(255,210,63,0.28)] hover:shadow-[0_0_44px_rgba(255,210,63,0.5)] transition-shadow mpl-lightning-glow"
        >
          <div className="flex items-center gap-4">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#FFD23F] to-[#FF8A00] text-black shadow-[0_0_18px_rgba(255,210,63,0.7)]">
              <Zap className="h-6 w-6" />
            </span>
            <div className="min-w-0">
              <p className="font-display text-xl tracking-tight text-[#FFE480] flex items-center gap-2">Lightning Request</p>
              <p className="text-xs text-white/60 leading-snug">No time to build the approval? <span className="text-[#FFE480] font-semibold underline">Click here</span> and your lender does it — lightning fast. ⚡</p>
            </div>
          </div>
        </button>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Left: client + form */}
        <div className="lg:col-span-3 space-y-6">
          <div className="bg-[#141414]/80 backdrop-blur-xl border border-white/10 shadow-2xl shadow-black/40 rounded-2xl p-6 mpl-rise">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h1 className="font-display text-3xl tracking-tight break-words">{client.borrower_name}</h1>
                <p className="text-white/50 text-sm break-words">{client.borrower_email}</p>
              </div>
              {isLenderView && (
                <div className="shrink-0 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={openEdit}
                    data-testid="edit-preapproval-button"
                    title="Edit pre-approval"
                    className="flex h-9 items-center gap-1.5 rounded-lg border border-white/15 px-3 text-xs font-medium text-white/70 hover:text-white hover:border-white/30 transition-colors"
                  >
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </button>
                  <button
                    type="button"
                    onClick={deleteClient}
                    disabled={deleting}
                    data-testid="delete-client-button"
                    title="Delete client"
                    className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 text-white/40 hover:text-[#ff3b30] hover:border-[#ff3b30]/40 transition-colors disabled:opacity-40"
                  >
                    {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                  </button>
                </div>
              )}
            </div>
            <span
              className={`mt-3 inline-flex items-center gap-1.5 text-[10px] tracking-[0.12em] uppercase px-2.5 py-1 rounded-full border ${statusMeta(client.status).cls}`}
              data-testid="client-detail-status"
            >
              <span className={`h-1.5 w-1.5 rounded-full ${statusMeta(client.status).dot}`} />
              {statusMeta(client.status).label}
            </span>
            {client.borrower_phone && (
              <p className="text-white/50 text-sm flex items-center gap-1.5 mt-0.5" data-testid="borrower-phone">
                <Phone className="h-3.5 w-3.5" /> {client.borrower_phone}
              </p>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6">
              {[
                ["Max Price", money(client.max_purchase_price)],
                ["Rate", `${client.interest_rate}%`],
                ["Term", `${client.loan_term_years} yr`],
                ["Down", `${client.down_payment_pct}%`],
              ].map(([k, v]) => (
                <div key={k}>
                  <p className="text-[10px] tracking-[0.2em] uppercase text-white/40">{k}</p>
                  <p className="font-display text-lg mt-1 mpl-tabular">{v}</p>
                </div>
              ))}
            </div>
            <p className="text-xs text-white/40 mt-4">{client.loan_type}</p>
            <div className="mt-4 pt-4 border-t border-white/10">
              <ToggleRow
                label="Enrolled in 30-day auto-update"
                desc="Email the borrower a friendly nudge 30 days after each pre-approval"
                checked={autoCheckin}
                onChange={toggleAutoCheckin}
                testid="toggle-auto-checkin"
                showState
              />
            </div>
          </div>

          {(blocked ? (
            <div className="bg-white text-black rounded-2xl p-6 flex items-start gap-3" data-testid="blocked-banner">
              <Ban className="h-5 w-5 mt-0.5 text-[#ff3b30] shrink-0" />
              <div>
                <p className="font-semibold">
                  {status.status !== "active" ? "Account pending approval" : "Some items have been updated"}
                </p>
                <p className="text-sm text-black/70">
                  Contact {status.lender_name || "your lender"}
                  {status.lender_phone ? ` at ${status.lender_phone}` : ""} to get more details.
                </p>
              </div>
            </div>
          ) : (
            <form onSubmit={generate} className="bg-[#141414]/80 backdrop-blur-xl border border-white/10 shadow-2xl shadow-black/40 rounded-2xl p-6 space-y-5 mpl-rise">
              <h2 className="font-display text-2xl tracking-tight">Generate pre-approval</h2>
              <Field
                label="Property address"
                value={form.address}
                onChange={set("address")}
                required
                placeholder="123 Palm Ave, Miami, FL"
                data-testid="input-address"
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field
                  label="Offer price"
                  prefix="$"
                  inputMode="numeric"
                  value={form.offer_price}
                  onChange={setMoney("offer_price")}
                  required
                  placeholder="750,000"
                  data-testid="input-offer-price"
                />
                <Field
                  label="Property taxes (annual)"
                  prefix="$"
                  inputMode="numeric"
                  value={form.property_taxes}
                  onChange={setMoney("property_taxes")}
                  placeholder="9,000"
                  data-testid="input-property-taxes"
                />
                <Field
                  label="HOA (monthly)"
                  prefix="$"
                  inputMode="numeric"
                  value={form.hoa}
                  onChange={setMoney("hoa")}
                  placeholder="250"
                  data-testid="input-hoa"
                />
                <Field
                  label="Seller concessions (optional)"
                  prefix="$"
                  inputMode="numeric"
                  value={form.concessions}
                  onChange={setMoney("concessions")}
                  placeholder="0"
                  data-testid="input-seller-concessions"
                />
              </div>

              <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 space-y-3" data-testid="concession-helper">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold leading-snug">Max seller concessions you can request</p>
                    <p className="text-[11px] text-white/45 mt-0.5">
                      {maxPct}% of the {offerNum > 0 ? "offer" : "max purchase"} price for {client.loan_type || "this loan"}
                      {client.occupancy && client.occupancy !== "primary" ? ` · ${client.occupancy}` : ""}
                    </p>
                  </div>
                  <span className="font-display text-xl tracking-tight text-emerald-300 mpl-tabular shrink-0 whitespace-nowrap" data-testid="max-concession-amount">
                    {basisPrice > 0 ? money(maxConcession) : "—"}
                  </span>
                </div>
                <Button
                  type="button"
                  onClick={() => setShowBreakdown(true)}
                  variant="outline"
                  data-testid="see-fee-breakdown-button"
                  className="h-auto min-h-11 w-full border-white/20 bg-transparent text-white hover:bg-white/10 rounded-lg text-sm py-2 whitespace-normal text-left justify-start"
                >
                  <FileText className="h-4 w-4 mr-2 shrink-0" /> See breakdown of fees to calculate total concessions needed
                </Button>
                <p className="text-[11px] text-white/40 mt-3 leading-relaxed" data-testid="concession-disclaimer">
                  <span className="text-white/55 font-semibold">Note:</span> Seller concessions cannot exceed the buyer's total closing costs — the buyer can't receive cash back at closing.
                </p>
              </div>

              <div className="space-y-3 border-t border-white/10 pt-4">
                <ToggleRow
                  label="Email pre-approval to borrower"
                  desc={client.borrower_email}
                  checked={toggles.sendPre}
                  onChange={(v) => setToggles({ ...toggles, sendPre: v })}
                  testid="toggle-send-preapproval"
                />
                {toggles.sendPre && (
                  <div className="pt-1">
                    <Label className="text-xs tracking-[0.15em] uppercase text-white/50">CC email(s) — optional</Label>
                    <Input
                      value={form.cc}
                      onChange={set("cc")}
                      placeholder="agent@brokerage.com, spouse@email.com"
                      className="mt-2 bg-black/40 border-white/15 h-11 text-white focus:ring-2 focus:ring-[#ff3b30]"
                      data-testid="input-cc-emails"
                    />
                    <p className="text-[11px] text-white/35 mt-1.5">We'll copy these people on the borrower email. Separate with commas.</p>
                  </div>
                )}
              </div>

              {overMax ? (
                <div className="space-y-3" data-testid="over-max-block">
                  {isLenderView ? (
                    <>
                      <div className="rounded-xl border border-[#FFD23F]/40 bg-[#FFD23F]/10 p-4 text-sm text-white/85">
                        That offer is above {client.borrower_name}'s current approved max of{" "}
                        <strong>{money(client.max_purchase_price)}</strong>. As the lender, you can raise the max and generate this approval now.
                      </div>
                      <Button
                        type="button"
                        onClick={raiseMaxAndGenerate}
                        disabled={requestingMax || loading}
                        data-testid="raise-max-button"
                        className="w-full h-12 font-extrabold text-black bg-gradient-to-r from-[#FFD23F] to-[#FF8A00] hover:brightness-110 rounded-xl transition"
                      >
                        {requestingMax || loading ? <Loader2 className="h-4 w-4 animate-spin" /> : `Raise max to ${money(toNum(form.offer_price))} & generate`}
                      </Button>
                    </>
                  ) : (
                    <>
                      <div className="rounded-xl border border-[#ff3b30]/40 bg-[#ff3b30]/10 p-4 text-sm text-white/85">
                        That offer is above {client.borrower_name}'s approved max of{" "}
                        <strong>{money(client.max_purchase_price)}</strong>. Ask your lender to raise it — we'll email them the request now.
                      </div>
                      <Button
                        type="button"
                        onClick={requestMaxIncrease}
                        disabled={requestingMax}
                        data-testid="request-max-button"
                        className="w-full h-12 bg-[#ff3b30] hover:bg-[#ff5147] text-white font-semibold rounded-xl transition-colors"
                      >
                        {requestingMax ? <Loader2 className="h-4 w-4 animate-spin" /> : "Request higher approval from your lender"}
                      </Button>
                    </>
                  )}
                </div>
              ) : (
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="generate-preapproval-button"
                  className="w-full h-12 bg-[#ff3b30] hover:bg-[#ff5147] text-white font-semibold rounded-xl transition-colors"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Generate pre-approval"}
                </Button>
              )}
            </form>
          ))}
        </div>

        {/* Right: result */}
        <div className="lg:col-span-2">
          <div className="lg:sticky lg:top-24">
            {result ? (
              <div className="bg-[#141414]/80 backdrop-blur-xl border border-white/10 shadow-2xl shadow-black/40 rounded-2xl p-6 mpl-rise" data-testid="preapproval-result">
                <div className="flex items-center gap-2 text-[#ff3b30]">
                  <CheckCircle2 className="h-5 w-5" />
                  <span className="text-xs tracking-[0.2em] uppercase">Issued</span>
                </div>
                <p className="text-[10px] tracking-[0.2em] uppercase text-white/40 mt-5">Estimated monthly payment</p>
                <p className="font-display text-4xl tracking-tighter mt-1 mpl-tabular" data-testid="monthly-payment">
                  {moneyCents(result.monthly_payment)}
                </p>

                <div className="mt-6 space-y-3 text-sm">
                  <Row k="Offer price" v={money(result.offer_price)} />
                  <Row k="Loan amount" v={money(result.loan_amount)} />
                  <Row k="Expires (90 days)" v={result.expires_at} />
                </div>

                <div className="mt-5 rounded-xl bg-black/40 border border-white/10 p-4" data-testid="payment-breakdown">
                  <p className="text-[10px] tracking-[0.2em] uppercase text-white/45 mb-3">Monthly payment breakdown</p>
                  <div className="space-y-2.5 text-sm">
                    <Row k="Principal & Interest (P&I)" v={moneyCents(result.principal_interest)} />
                    <Row k="Property taxes" v={moneyCents((result.property_taxes || 0) / 12)} />
                    <Row k="Homeowners insurance" v={moneyCents(result.insurance_monthly || 0)} />
                    <Row k="HOA" v={moneyCents(result.hoa || 0)} />
                  </div>
                  <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-between">
                    <span className="text-xs tracking-[0.15em] uppercase text-white/55">Total / month</span>
                    <span className="font-display text-xl text-[#ff3b30] mpl-tabular" data-testid="breakdown-total">
                      {moneyCents(result.monthly_payment)}
                    </span>
                  </div>
                </div>

                {result.over_max && (
                  <div className="mt-5 flex items-start gap-2 bg-[#ff3b30]/10 border border-[#ff3b30]/30 rounded-lg p-3 text-sm">
                    <AlertTriangle className="h-4 w-4 mt-0.5 text-[#ff3b30] shrink-0" />
                    <span className="text-white/80">
                      Offer exceeds the approved max of {money(result.max_purchase_price)}. Lower the offer to stay within approval.
                    </span>
                  </div>
                )}

                <div className="mt-6 grid grid-cols-1 gap-3">
                  <Button
                    onClick={() => setPreview({ path: `/preapprovals/${result.id}/pdf`, filename: `preapproval-${client.borrower_name}.pdf`, title: "Pre-approval preview" })}
                    variant="outline"
                    data-testid="preview-approval-button"
                    className="h-11 border-white/20 bg-transparent text-white hover:bg-white/10 rounded-xl"
                  >
                    <Eye className="h-4 w-4 mr-2" /> Preview pre-approval
                  </Button>

                  <a
                    href={smsHref}
                    data-testid="text-preapproval-button"
                    className="inline-flex items-center justify-center h-12 bg-[#ff3b30] hover:bg-[#ff5147] text-white font-semibold rounded-xl transition-colors"
                  >
                    <MessageSquare className="h-4 w-4 mr-2" /> Click here to text
                  </a>

                  <a
                    href={mailHref}
                    data-testid="email-preapproval-button"
                    className="inline-flex items-center justify-center h-11 border border-white/20 text-white hover:bg-white/10 rounded-xl transition-colors"
                  >
                    <Mail className="h-4 w-4 mr-2" /> Click here to email
                  </a>

                  <Button
                    onClick={downloadPdf}
                    disabled={busy === "pdf"}
                    data-testid="download-pdf-button"
                    className="h-11 bg-white text-black hover:bg-white/90 font-semibold rounded-xl"
                  >
                    {busy === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Download className="h-4 w-4 mr-2" /> Download pre-approval PDF</>}
                  </Button>
                  {canLoanEstimate && result.generate_loan_estimate && (
                    <Button
                      onClick={downloadEstimate}
                      disabled={busy === "est"}
                      variant="outline"
                      data-testid="download-estimate-button"
                      className="h-11 border-white/20 bg-transparent text-white hover:bg-white/10 rounded-xl"
                    >
                      {busy === "est" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Download className="h-4 w-4 mr-2" /> Download loan estimate PDF</>}
                    </Button>
                  )}
                  <Button
                    onClick={emailSelf}
                    disabled={busy === "email"}
                    variant="outline"
                    data-testid="email-self-button"
                    className="h-11 border-white/20 bg-transparent text-white hover:bg-white/10 rounded-xl"
                  >
                    {busy === "email" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Mail className="h-4 w-4 mr-2" /> Email a copy to me</>}
                  </Button>
                </div>
                <p className="text-xs text-white/40 mt-4">
                  Tap <b>text</b> or <b>email</b> and your phone opens with the link ready — send it to the buyer, the realtor, or as many people as you like.
                </p>
              </div>
            ) : (
              <div className="border border-dashed border-white/15 rounded-2xl p-8 text-center">
                <p className="text-white/45 text-sm">
                  Fill in the offer details and the pre-approval will appear here — ready to download or email.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mt-8">
        <Tabs defaultValue="approvals" className="w-full">
          <TabsList className="bg-[#141414]/80 border border-white/10 rounded-xl p-1 mb-5">
            <TabsTrigger value="approvals" data-testid="tab-approvals" className="data-[state=active]:bg-white data-[state=active]:text-black rounded-lg px-4 text-sm">
              Past approvals
            </TabsTrigger>
            <TabsTrigger value="lightning" data-testid="tab-lightning" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#FFD23F] data-[state=active]:to-[#FF8A00] data-[state=active]:text-black rounded-lg px-4 text-sm">
              <Zap className="h-3.5 w-3.5 mr-1" /> Lightning{pendingLightning ? ` (${pendingLightning})` : ""}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="approvals">
            <ClientLedger clientId={id} borrowerName={client.borrower_name} borrowerEmail={client.borrower_email} borrowerPhone={client.borrower_phone} version={ledgerVersion} showEstimate={canLoanEstimate} lightningOnly={false} />
          </TabsContent>

          <TabsContent value="lightning">
            <div className="space-y-6">
              <div>
                <h3 className="font-display text-lg tracking-tight mb-3 flex items-center gap-2"><Zap className="h-4 w-4 text-[#FFD23F]" /> Lightning requests</h3>
                <LightningRequests requests={lightningReqs} isLender={isLenderView} onBuild={buildFromRequest} onApproveBuild={approveAndBuild} />
              </div>
              <div>
                <h3 className="font-display text-lg tracking-tight mb-3">Lightning approvals</h3>
                <ClientLedger clientId={id} borrowerName={client.borrower_name} borrowerEmail={client.borrower_email} borrowerPhone={client.borrower_phone} version={ledgerVersion} showEstimate={canLoanEstimate} lightningOnly={true} />
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <LightningRequestDialog
        clientId={id}
        open={showLightning}
        onClose={() => setShowLightning(false)}
        onSent={loadLightning}
      />

      <PdfPreviewModal
        open={!!preview}
        onClose={() => setPreview(null)}
        path={preview?.path}
        filename={preview?.filename}
        title={preview?.title}
      />
      <FeeBreakdownModal
        open={showBreakdown}
        onClose={() => setShowBreakdown(false)}
        client={client}
        maxPct={maxPct}
        maxAmount={basisPrice > 0 ? maxConcession : null}
      />

      <Dialog open={showEdit} onOpenChange={(v) => (!v ? setShowEdit(false) : null)}>
        <DialogContent className="bg-[#0f0f0f]/95 backdrop-blur-xl border-white/10 text-white max-w-md" data-testid="edit-preapproval-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tight flex items-center gap-2">
              <Pencil className="h-5 w-5 text-[#ff3b30]" /> Edit pre-approval
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-white/55 -mt-1">Bump the loan amount or terms — no need to start over. Generate a fresh pre-approval after saving.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <EditField label="Max purchase price" prefix="$" inputMode="numeric" value={editForm.max_purchase_price || ""} onChange={setEdit("max_purchase_price", true)} testid="edit-max-price" />
            <EditField label="Interest rate (%)" inputMode="decimal" value={editForm.interest_rate || ""} onChange={setEdit("interest_rate")} testid="edit-rate" />
            <EditField label="APR (%)" inputMode="decimal" value={editForm.apr || ""} onChange={setEdit("apr")} testid="edit-apr" />
            <EditField label="Down payment (%)" inputMode="decimal" value={editForm.down_payment_pct || ""} onChange={setEdit("down_payment_pct")} testid="edit-down" />
            <EditField label="Loan term (years)" inputMode="numeric" value={editForm.loan_term_years || ""} onChange={setEdit("loan_term_years")} testid="edit-term" />
            <EditField label="Insurance / mo" prefix="$" inputMode="numeric" value={editForm.insurance_monthly || ""} onChange={setEdit("insurance_monthly", true)} testid="edit-insurance" />
          </div>
          <EditField label="Loan type" value={editForm.loan_type || ""} onChange={setEdit("loan_type")} testid="edit-loan-type" />
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setShowEdit(false)} className="h-11 border-white/20 bg-transparent text-white hover:bg-white/10 rounded-xl px-5">Cancel</Button>
            <Button onClick={saveEdit} disabled={savingEdit} data-testid="edit-save-button" className="h-11 bg-[#ff3b30] hover:bg-[#ff5147] text-white font-semibold rounded-xl px-6">
              {savingEdit ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}

function EditField({ label, prefix, testid, ...props }) {
  return (
    <div className="space-y-2">
      <Label className="text-xs tracking-[0.15em] uppercase text-white/50">{label}</Label>
      <div className="relative">
        {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 text-sm">{prefix}</span>}
        <Input {...props} data-testid={testid} className={`bg-black/40 border-white/15 h-11 text-white focus:ring-2 focus:ring-[#ff3b30] ${prefix ? "pl-7" : ""}`} />
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-center justify-between border-b border-white/8 pb-2">
      <span className="text-white/50">{k}</span>
      <span className="font-semibold mpl-tabular">{v}</span>
    </div>
  );
}

function ToggleRow({ label, desc, checked, onChange, testid, showState }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div>
        <p className="text-sm font-medium">{label}</p>
        {desc && <p className="text-xs text-white/40">{desc}</p>}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {showState && <span className="text-xs text-white/50">{checked ? "On" : "Off"}</span>}
        <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
      </div>
    </div>
  );
}
